/** Users at the caller's site(s) and ancestor sites (from lookup.sites parentId). */
/** Walk lookup.sites parentId from each starting site. */
export function collectAncestorSiteIds(sites, startSiteIds) {
    const byId = new Map();
    for (const site of sites) {
        if (typeof site.id === 'number' && site.id > 0)
            byId.set(site.id, site);
    }
    const scoped = new Set();
    const queue = [];
    for (const id of startSiteIds) {
        if (typeof id === 'number' && Number.isFinite(id) && id > 0)
            queue.push(Math.trunc(id));
    }
    while (queue.length > 0) {
        const id = queue.pop();
        if (id == null || scoped.has(id))
            continue;
        scoped.add(id);
        const parentId = byId.get(id)?.parentId;
        if (typeof parentId === 'number' && parentId > 0)
            queue.push(parentId);
    }
    return scoped;
}
/** Kokomo status 0 (or omitted) is active. */
export function isActiveLookupUser(user) {
    return user.status == null || user.status === 0;
}
/**
 * Users assigned to `scopedSiteIds`, plus org-level users with no siteId.
 * Empty scope means we cannot narrow — return all active users.
 */
export function usersAtSitesAndParents(users, scopedSiteIds) {
    const active = users.filter((user) => user.id > 0 && isActiveLookupUser(user));
    if (scopedSiteIds.size === 0)
        return active;
    return active.filter((user) => {
        const siteId = user.siteId;
        if (typeof siteId !== 'number' || siteId <= 0)
            return true;
        return scopedSiteIds.has(siteId);
    });
}
export function lookupUserOptionLines(user) {
    const name = [user.firstName, user.lastName].map((part) => part?.trim()).filter(Boolean).join(' ');
    const email = user.email?.trim() ?? '';
    return {
        name: name || email || `User ${user.id}`,
        email: name ? email : '',
        meta: [user.siteName?.trim(), user.roleName?.trim()].filter(Boolean).join(' · '),
    };
}
export function lookupUserDisplayName(user) {
    const lines = lookupUserOptionLines(user);
    const extras = [lines.email, lines.meta].filter(Boolean);
    return extras.length > 0 ? `${lines.name} · ${extras.join(' · ')}` : lines.name;
}
export function sortLookupUsers(users) {
    return [...users].sort((a, b) => {
        const nameA = `${a.lastName ?? ''} ${a.firstName ?? ''}`.trim() || a.email || String(a.id);
        const nameB = `${b.lastName ?? ''} ${b.firstName ?? ''}`.trim() || b.email || String(b.id);
        const cmp = nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
        if (cmp !== 0)
            return cmp;
        return a.id - b.id;
    });
}
